import BaseWorldScene, {AppContext, GamePlayer} from "./BaseWorldScene";
import {CompatClient, Stomp} from "@stomp/stompjs";
import Phaser, {Scene} from "phaser";
import {ConferenceEvent} from "../DataTypes";
import {float, smooth} from "./stuff";
import {TutorialPhase} from "./tutorial/TutorialWorldScene";
import moment from "moment";
import VenueDefinition from "./VenueDefinition";
import {confappdata} from "confappdata";
type Player = confappdata.Player;

type Tilemap = Phaser.Tilemaps.Tilemap;
type Group = Phaser.Physics.Arcade.Group

export interface LiveAppContext extends AppContext {
    selectedEvent: ConferenceEvent

    isAudioActive(): boolean

    setSendMessageCallback(callback: (message: string) => void): void

    showMessage(message: ChatMessage, playerInfo: PlayerAndInfo): void

    showVideoChat(chatName: string): void

    hideVideoChat(): void

    setTwilioToken(token: any): void

    getVideoChatRoomName(): string | null

    getGameOverlayMessage(): string | null

    setGameOverlayMessage(messag: OverlayMessage): void
}

export class OverlayMessage {
    constructor(
        public title: string,
        public body: string,
        public phase: TutorialPhase | null,
        public callback: () => void) {
    }
}

interface PlayerMap {
    [index: string]: Player;
}

export interface PlayerAndInfoMap {
    [index: number]: PlayerAndInfo;
}

export interface PlayerCount {
    [index: number]: number;
}

export interface ChatMessage {
    messageKey: string
    userId: number
    message: string
}

export default class LiveWorldScene extends BaseWorldScene {
    stompClient: CompatClient | null = null
    otherPlayers: Phaser.Physics.Arcade.Group | null = null
    stompUserId = ""
    startTime: number = Date.now()
    allPlayers: PlayerAndInfoMap = {}
    movements: number[][] = []
    movementTrackerIntervalId: NodeJS.Timeout | null = null
    dateFormat = moment.localeData().longDateFormat('L')
    lastHeartbeat = new Date().getTime()
    otherPlayerCount:PlayerCount = {}

    constructor(
        public worldKey: string,
        public mapKey: string,
        public backOnBounds: boolean,
        public appContext: LiveAppContext,
        venue: VenueDefinition) {
        super(worldKey, mapKey, backOnBounds, appContext, venue);
    }

    create() {
        super.create();
        if(this.stompClient)
            throw new Error("Scene already live")
        this.sockJsConnect()
        this.startMovementTracker()
    }

    processMovement(containerBody: Phaser.Physics.Arcade.Body) {
        if (this.appContext.getVideoChatRoomName()) {
            this.gamePlayer!.player.anims.stop();
        } else {
            super.processMovement(containerBody);
        }
    }

    processMapFeatures(map: Tilemap) {
        super.processMapFeatures(map);
        map.filterObjects("Objects", (obj) => {
            return obj.type === "ChatNode"
        }).forEach((obj) => this.makeChatNode(obj as Phaser.GameObjects.Shape))
    }

    makeChatNode(obj: Phaser.GameObjects.Shape) {
        let playTable = this.physics.add.staticGroup()
        let tableSprite = playTable.create(obj.x, obj.y, "tv", 0) as Phaser.GameObjects.Sprite
        tableSprite.anims.play('tv_playing', true);
        tableSprite.setInteractive();

        this.addCollide(tableSprite, () => {
            this.appContext.showVideoChat(obj.name)
            this.onDoneTouching = () => this.appContext.hideVideoChat()
        })
    }

    fetchTwilio() {
        fetch(`${process.env.REACT_APP_GAME_CALL}/user/accessToken/${this.appContext.selectedEvent.id}/${this.worldKey}/${this.stompUserId}`)
            .then(response => response.json())
            .then(response => {
                this.appContext.setTwilioToken(response.jwt)
            })
    }

    sockJsConnect() {
        this.stompClient = Stomp.client(`${process.env.REACT_APP_GAME_CALL_WS}/gs-guide-websocket`)
        this.stompClient.reconnect_delay = 5000
        this.stompClient.debug = function () {
        }; //Stop debug logging

        this.callConnect()
    }

    callConnect() {
        logTime("callConnect")

        // this.stompClient.debug = null
        let headers = this.connectHeaders(this.currentX, this.currentY);

        this.stompClient!.connect(headers, (frame: any) => {
            // console.log("connecting ...")

            //Delete all player icons
            Object.keys(this.allPlayers).forEach((key) => {
                let player = this.allPlayers[parseInt(key)]
                if (player && !player.isMe) {
                    deleteOtherUser(this, player)
                }
            })

            if(this.otherPlayers){
                this.otherPlayers.destroy()
            }

            this.allPlayers = {}

            this.otherPlayers = this.physics.add.group();
            this.stompUserId = frame['headers']['user-name']

            //Server will call this when connect completes
            this.stompClient!.subscribe('/user/topic/currentPlayers/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {

                this.fetchTwilio()

                let players = JSON.parse(msg.body) as PlayerMap
                Object.keys(players).forEach((id) => {
                    if (players[id].id === this.appContext.currentUserId) {
                        this.storePlayerInfo(this.addMyPlayer(players[id]))
                    } else {
                        this.storePlayerInfo(this.addOtherPlayer(players[id]))
                    }
                })
            })

            this.stompClient!.subscribe('/topic/newPlayer/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let playerInfo = JSON.parse(msg.body)

                logTime(`newPlayer(${playerInfo.id})`)

                if(playerInfo.id !== this.appContext.currentUserId) //Don't add me
                {
                    let currentPlayer = this.allPlayers[playerInfo.id]
                    if(currentPlayer)
                        deleteOtherUser(this, currentPlayer)
                    this.storePlayerInfo(this.addOtherPlayer(playerInfo))
                }
            })

            //Can't we just remove by key?
            this.stompClient!.subscribe('/topic/disconnect/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let userId = parseInt(msg.body)
                let player = this.allPlayers[userId]

                if(player)
                    logTime(`disconnect(${userId})`)
                else
                    logTime(`disconnect(unknown)`)

                if (player) {
                    deleteOtherUser(this, player)
                }
            })

            this.stompClient!.subscribe('/topic/playerMoved/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let playerMovementContainer = JSON.parse(msg.body)
                if (playerMovementContainer.userId === this.appContext.currentUserId)//Needs current user id
                    return

                let playerInfoArray = playerMovementContainer.playerMovements;
                let player = this.allPlayers[playerMovementContainer.userId]
                if (player) {
                    player.scheduleMovements(playerInfoArray)
                }
            })

            this.stompClient!.subscribe('/user/topic/hearMessage/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let sentMessage = JSON.parse(msg.body)
                const playerInfo = this.allPlayers[sentMessage.userId]

                if (this.appContext.isAudioActive()) {
                    this.clunk!.play({
                        loop: false
                    });
                }

                this.appContext.showMessage(sentMessage, playerInfo)
            })

            this.stompClient!.subscribe('/topic/playerTyping/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let userId = parseInt(msg.body)
                let player = this.allPlayers[userId]
                if (player && !player.isMe) {
                    player.showTyping()
                }
            });

            this.stompClient!.subscribe('/topic/playerDoneTyping/' +
                this.appContext.selectedEvent.id + '/' +
                this.worldKey, (msg) => {
                let userId = parseInt(msg.body)
                let player = this.allPlayers[userId]
                if (player && !player.isMe) {
                    player.hideTyping()
                }
            });

            this.appContext.setSendMessageCallback((message: string) => {
                this.stompClient!.send("/app/sendMessage", this.worldKeyHeader(), message)
            })

            //Do this last
            this.stompClient!.send("/app/connection", this.connectHeaders(this.currentX, this.currentY), "connect")

            /*setTimeout(() => {
                this.stompClient!.disconnect()
                setTimeout(() => {
                    this.callConnect()
                }, 500)
            }, 10000)*/
        });
    }


    connectHeaders(playerX: number, playerY: number) {
        return {
            type: "scene",
            playerX: playerX,
            playerY: playerY,
            id: this.appContext.currentUserId,
            ...this.worldKeyHeader()
        }
    }

    sendTypingCallbacks = smooth(() => {
        this.stompClient!.send("/app/typing", this.worldKeyHeader(), "empty")
    }, 1000)

    worldKeyHeader() {
        return {
            eventId: this.appContext.selectedEvent.id,
            worldKey: this.worldKey
        }
    }

    otherPlayersIter(func: (p: PlayerAndInfo) => void) {
        Object.keys(this.allPlayers).forEach((key) => {
            const playerAndInfo = this.allPlayers[parseInt(key)]
            if (!playerAndInfo.isMe) {
                func(playerAndInfo)
            }
        })
    }

    storePlayerInfo(playerInfo: PlayerAndInfo) {
        this.allPlayers[playerInfo.lastPlayerInfo.id] = playerInfo
    }

    shutDown() {
        super.shutDown()
        const id = this.movementTrackerIntervalId
        if (id){
            this.sendMovements()
            clearInterval(id)
        }
        this.stompClient!.disconnect()
        this.sendTypingCallbacks.clear()
    }

    addMyPlayer(playerInfo: Player) {
        return new PlayerAndInfo(
            this.gamePlayer!,
            true,
            playerInfo,
            () => {
            },
            () => {
            },
            () => {
            },
            () => `${process.env.REACT_APP_API_CALL}/image?id=${playerInfo.id}`
        )
    }

    plusPlayerCount(playerId: number){
        let playerCount : number | null = this.otherPlayerCount[playerId]
        if(!playerCount){
            playerCount = 0
        }
        playerCount++
        if(playerCount > 1)
            throw new Error(`playerId(${playerId}), playerCount > 1 (${playerCount})`)

        this.otherPlayerCount[playerId] = playerCount
    }

    minusPlayerCount(playerId: number){
        let playerCount : number | null = this.otherPlayerCount[playerId]
        if(!playerCount || playerCount < 1){
            throw new Error(`playerId(${playerId}), playerCount invalid ${playerCount}`)
        }
        playerCount--
        this.otherPlayerCount[playerId] = playerCount
    }

    addOtherPlayer(playerInfo: Player) {
        this.plusPlayerCount(playerInfo.id)

        let container = this.add.container(playerInfo.x, playerInfo.y);
        (container as any).myCount = containerCount++
        console.log(`addOtherPlayer: playerId=${playerInfo.id}, myCount=${(container as any).myCount}`)
        container.setSize(32, 32);
        this.physics.world.enable(container);

        container.on('pointerdown', function () {

        });

        const otherPlayer = this.add.sprite(0, 0, 'player', 9);
        container.add(otherPlayer)

        let chatBubble = this.add.sprite(2, -40, 'chatBubbles', 0)
        container.add(chatBubble)
        chatBubble.anims.play('talking', true);
        chatBubble.visible = false
        chatBubble.setDepth(1550)

        this.addPlayerHead(container, playerInfo.id, chatBubble)

        const hideTyping = () => chatBubble.visible = false

        const touchTyping = () => {
            if (chatBubble.visible) {
                let distance = Math.sqrt(Math.pow(container.x - this.gamePlayer!.container!.x, 2) + Math.pow(container.y - this.gamePlayer!.container!.y, 2))
                if (distance < 90) {
                    chatBubble.alpha = 1
                } else {
                    chatBubble.alpha = .3
                }
            }
        }

        const showTyping = float(
            () => {
                chatBubble.visible = true
                touchTyping()
            }
            ,
            () => hideTyping(),
            5000
        )

        this.otherPlayers!.add(container);
        const gamePlayer = new GamePlayer(container, otherPlayer, null, chatBubble)
        return new PlayerAndInfo(
            gamePlayer,
            false,
            playerInfo,
            touchTyping,
            showTyping,
            hideTyping,
            () => `${process.env.REACT_APP_API_CALL}/image?id=${playerInfo.id}`
            )
    }

    imTalkin = () => {
        this.gamePlayer!.talkRadius!.visible = true
        this.gamePlayer!.chatBubble.visible = true
        this.sendTypingCallbacks.trigger()
    }

    imNotTalkin = () => {
        this.gamePlayer!.talkRadius!.visible = false
        this.gamePlayer!.chatBubble!.visible = false
        const sc = this.stompClient
        if(sc && sc.active){
            sc.send("/app/doneTyping", this.worldKeyHeader(), "empty")
        }
        this.sendTypingCallbacks.clear()
    }

    playerMovement(x: number, y: number, flipX: boolean) {
        super.playerMovement(x, y, flipX)
        this.movements.push([
            Math.max(0, Date.now() - this.startTime),
            Math.floor(x),
            Math.floor(y),
            flipX ? 1 : 0
        ])

        this.otherPlayersIter((player) => player.touchTyping())
    }

    startMovementTracker() {
        this.startTime = Date.now()
        this.movements = []
        this.movementTrackerIntervalId = setInterval(() => {
            this.sendMovements()
        }, 500)
    }

    sendMovements() {
        if (this.movements.length > 0) {
            this.stompClient!.send("/app/playerMovement", this.worldKeyHeader(), JSON.stringify(this.movements))
            this.movements = []
        }
        this.startTime = Date.now()
    }

    otherUpdate() {
        const start = Date.now()
        Object.keys(this.allPlayers).forEach((userId) => {
            const player = this.allPlayers[parseInt(userId)]
            player.playMovements(start)
        })

        const now = new Date().getTime()
        if((this.lastHeartbeat + (1000 * 15)) < now){
            logTime("heartbeat")
            if(this.stompClient && this.stompClient!.connected){
                this.stompClient!.send("/app/heartbeat", this.worldKeyHeader())
                this.lastHeartbeat = now
            }
        }
    }
}

const start = new Date()

function logTime(name:string){
    console.log(`${name} - ${printTime()}`)
}

function printTime(d = new Date()){
    return `h: ${d.getHours()} m:${d.getMinutes()} s:${d.getSeconds()} mi:${d.getMilliseconds()}`
}

interface ManagedSceneObject<T extends Scene> {

}

function deleteOtherUser(scene: LiveWorldScene, player: PlayerAndInfo): void {
    scene.minusPlayerCount(player.lastPlayerInfo.id)
    let container = player.gamePlayer.container
    console.log(`deleteOtherUser: playerId=${player.lastPlayerInfo.id}, myCount=${(container as any).myCount}`)

    player.gamePlayer.container.destroy();
    delete scene.allPlayers[player.lastPlayerInfo.id]
}

export class PlayerAndInfo {

    movements:number[][] = []

    constructor(
        public gamePlayer: GamePlayer,
        public isMe: boolean,
        public lastPlayerInfo: Player,
        public touchTyping: () => void,
        public showTyping: () => void,
        public hideTyping: () => void,
        public profileUrl: () => string
    ) {
    }

    scheduleMovements(m:number[][]){
        const start = Date.now()
        const needSort = this.movements.length > 0
        m.forEach((mov) => {
            mov[0] = mov[0] + start
            this.movements.push(mov)
        })
        if(needSort){
            this.movements.sort((a, b) => a[0] - b[0])
        }
    }

    playMovements(start:number){
        while (this.movements.length > 0 && this.movements[0][0] <= start){
            const playerInfo = this.movements[0]
            this.lastPlayerInfo.flipX = playerInfo[3] === 1;
            this.gamePlayer.container.setPosition(playerInfo[1], playerInfo[2]);
            this.touchTyping()
            this.movements.shift()
        }
    }
}

var containerCount = 0